import { CacheStrategy } from '@rammetec/data-access';
import { AUDIT_FIELD_DESCRIPTORS, AUDIT_FOREIGN_KEYS } from './_auditFields.js';
import { VERSION_FIELD_DESCRIPTOR } from './_versionField.js';
import { BUSINESS_ALLOWED_DATA_SOURCES, BUSINESS_DEFAULT_DATA_SOURCE } from './dataSourcePolicy.js';
import { PURCHASE_ORDER, PurchaseOrderStatusValues, PURCHASE_ORDER_STATUS_LABELS, } from '../types/purchase-order.js';
/**
 * Descriptor for the purchase order header (org/{orgId}/purchase-order).
 *
 * No lifecycle fields (a purchase order is transactional); the only lifecycle is
 * the business `status`. Keeps the `Auditable` block. `supplierId` is a FK to a
 * Party with the SUPPLIER role. Totals are computed in purchaseOrderService.
 */
export const purchaseOrderDescriptor = {
    name: PURCHASE_ORDER,
    label: 'Ordem de compra',
    domain: 'purchasing',
    collectionPath: 'org/{orgId}/purchase-order',
    pathPatternKeys: {
        orgId: 'string',
    },
    version: 1,
    description: 'Purchase order header with computed totals and a 4-state status machine',
    repositoryConfig: {
        dataSource: BUSINESS_DEFAULT_DATA_SOURCE,
        // NoCache: entidade versionada (spec 0045) — getById em cache devolve versão velha e anula a detecção de conflito.
        cache: CacheStrategy.NoCache,
    },
    allowedDataSources: BUSINESS_ALLOWED_DATA_SOURCES,
    fields: [
        { name: 'id', label: 'ID', type: 'string', required: true, description: 'Unique purchase order identifier' },
        {
            name: 'code',
            example: 7712,
            label: 'Código',
            type: 'number',
            display: 'code',
            required: true,
            unique: true,
            autoNumber: true,
            description: 'Purchase order code (manual integer, unique per organization; positive, capped at MAX_CODE_SMALL)',
        },
        {
            name: 'supplierId',
            label: 'Fornecedor',
            type: 'string',
            required: true,
            description: 'Supplier party (SUPPLIER role)',
        },
        {
            name: 'orderDate',
            example: '2026-10-05',
            label: 'Data da ordem',
            type: 'date',
            required: false,
            description: 'Date of the purchase order',
        },
        {
            name: 'status',
            example: 'confirmed',
            label: 'Status',
            type: 'enum',
            required: true,
            enumValues: [...PurchaseOrderStatusValues],
            enumLabels: PURCHASE_ORDER_STATUS_LABELS,
            description: 'Purchase order status (draft → confirmed → partially-received → received | cancelled)',
            defaultValue: 'draft',
        },
        {
            name: 'totalAmount',
            example: 6750,
            label: 'Valor total',
            type: 'number',
            display: 'money',
            required: false,
            description: 'Denormalized header total = Σ item.totalPrice (computed in the service)',
        },
        {
            name: 'itemCount',
            example: 1,
            label: 'Itens',
            type: 'number',
            display: 'quantity',
            required: false,
            description: 'Denormalized line count (computed in the service)',
        },
        ...AUDIT_FIELD_DESCRIPTORS,
        VERSION_FIELD_DESCRIPTOR,
    ],
    foreignKeys: [
        ...AUDIT_FOREIGN_KEYS,
        {
            fieldName: 'supplierId',
            targetEntity: 'Party',
            relationship: 'many-to-one',
            displayField: 'displayName',
            description: 'Supplier party (SUPPLIER role)',
        },
    ],
};
//# sourceMappingURL=purchase-order.descriptor.js.map